import {StandardSchemaV1} from '@standard-schema/spec';
import {dual, hole} from 'effect/Function';
import * as Pipeable from 'effect/Pipeable';
import {isObject, isRecord, isString} from 'effect/Predicate';
import {Covariant, Simplify} from 'effect/Types';

const WishTypeId = Symbol.for('wish/WishTypeId');

export interface Wish<out T> extends Wish.Callable<T>, Wish.Variance<T>, Pipeable.Pipeable {}

export namespace Wish {
    export const is = (thing: unknown): thing is Wish<any> => thing != null && Reflect.has(thing, WishTypeId);

    export const make = <T>(source: Wish.Callable<T>): Wish<T> => {
        if (Wish.is(source)) {
            return source;
        }
        return {...Proto, ...source, call: source.call.bind(source)};
    };

    export interface Callable<out T> {
        call(it: unknown): WishResult<T>;
    }

    export interface Variance<out T> {
        readonly [WishTypeId]: {readonly _T: Covariant<T>};
        readonly Type: T;
    }
    export const Proto: Pipeable.Pipeable & Variance<any> = {
        ...Pipeable.Prototype,
        [WishTypeId]: {_T: _ => _},
        Type: hole,
    };
}

export type Infer<D extends WishLike<any>> = D extends WishLike<infer T> ? T : never;

export type WishLike<T> = Wish.Callable<T> | StandardSchemaV1<any, T>;
export namespace WishLike {
    export const normalize = <T>(wishlike: WishLike<T>): Wish<T> => {
        if ('~standard' in wishlike) {
            return makeStandard(wishlike);
        }
        return Wish.make(wishlike);
    };
}

export type WishResult<T> = WishFail | WishCope<T> | WishOk<T>;

export namespace WishResult {
    export const mapOutput: {
        <T, U>(result: WishResult<T>, mapper: (value: T) => U): WishResult<U>;
        <T, U>(
            mapper: (value: T) => U
        ): {
            (result: WishResult<T>): WishResult<U>;
        };
    } = dual(2, <T, U>(result: WishResult<T>, mapper: (value: T) => U): WishResult<U> => {
        if (WishCope.is(result)) {
            return WishCope.make(result.issues, mapper(result.output));
        }
        if (WishOk.is(result)) {
            return WishOk.make(mapper(result.output));
        }
        return result;
    });
}

const WishResultTypeId = Symbol.for('wish/ResultTypeId');

export namespace WishResult {
    export const is = (thing: unknown): thing is WishResult<any> => isRecord(thing) && WishResultTypeId in thing;

    export interface StandardSchemaV1Options {
        readonly strict?: boolean;
    }

    export const coerce =
        <T>() =>
        <R extends WishResult<any>>(base: R): R & WishResult.Prototype<T> =>
            base;

    export const toStandardResult = <T>(
        result: WishResult<T>,
        {strict = true}: StandardSchemaV1Options = {}
    ): StandardSchemaV1.Result<T> => {
        if (!strict && WishCope.is(result)) {
            return {value: result.output};
        }
        if (WishFail.is(result)) {
            return {issues: result.issues};
        }
        return {value: result.output};
    };

    export const extend = <T extends WishFail | WishCope<any>>(key: Key, upper: T): T => {
        const issues: readonly WishIssue[] = upper.issues.map((i): WishIssue => ({...i, at: [key, ...i.at]}));
        return {
            ...upper,
            issues,
        };
    };

    export const aggregate = <T>(exits: Array<WishFail | WishCope<unknown>>, output: T) => {
        const {make} = exits.every(WishCope.is) ? WishCope : WishFail;
        return {
            ...make('', output),
            issues: exits.flatMap(e => e.issues),
        };
    };

    export const aggregateFail = (exits: Array<WishFail>) => {
        return {
            ...WishFail.make(''),
            issues: exits.flatMap(e => e.issues),
        };
    };

    export interface Prototype<T> extends Pipeable.Pipeable {
        readonly [WishResultTypeId]: {readonly _T: Covariant<T>};
    }
    export const Prototype: Prototype<never> = {
        ...Pipeable.Prototype,
        [WishResultTypeId]: {_T: _ => _},
    };
}

export interface WishOk<out T> extends WishResult.Prototype<T> {
    readonly _tag: 'Ok';
    readonly output: T;
}

export namespace WishOk {
    export const is = (thing: unknown): thing is WishOk<any> => WishResult.is(thing) && thing._tag === 'Ok';

    export const make = <T>(output: T): WishOk<T> => {
        return {
            ...WishResult.Prototype,
            _tag: 'Ok',
            output,
        };
    };
}

export interface WishFail extends WishResult.Prototype<never> {
    readonly _tag: 'Fail';
    readonly issues: ReadonlyArray<WishIssue>;
}

export namespace WishFail {
    export const is = (thing: unknown): thing is WishFail => WishResult.is(thing) && thing._tag === 'Fail';

    export const make = (messageOrIssues: string | ReadonlyArray<WishIssue>): WishFail => {
        return {
            ...WishResult.Prototype,
            _tag: 'Fail',
            issues: makeIssues(messageOrIssues),
        };
    };

    export const toStandardIssues = (self: WishFail) => {
        return {
            issues: self.issues.map(
                (i): StandardSchemaV1.Issue => ({
                    ...i,
                    path: i.at,
                })
            ),
        };
    };

    export const fromStandardIssues = (issues: ReadonlyArray<StandardSchemaV1.Issue>) => {
        return make(
            issues.map(
                (i): WishIssue => ({
                    message: i.message,
                    at: i.path?.map(s => (isObject(s) ? s.key : s)) ?? [],
                })
            )
        );
    };
}

interface WishIssue {
    readonly at: ReadonlyArray<Key>;
    readonly message: string;
}

export interface WishCope<out T> extends WishResult.Prototype<T> {
    readonly _tag: 'Cope';
    readonly output: T;
    readonly issues: ReadonlyArray<WishIssue>;
}

export namespace WishCope {
    export const is = (thing: unknown): thing is WishCope<any> => WishResult.is(thing) && thing._tag === 'Cope';

    export const make = <T>(messageOrIssues: string | ReadonlyArray<WishIssue>, output: T): WishCope<T> => {
        return {
            ...WishResult.Prototype,
            _tag: 'Cope',
            output,
            issues: makeIssues(messageOrIssues),
        };
    };
}

export const makeWishArray = <T>(source: WishLike<T>): Wish<readonly T[]> => {
    const wish = WishLike.normalize(source);
    return Wish.make(function wishArray(this: unknown, thing: unknown = this): WishResult<ReadonlyArray<T>> {
        if (!Array.isArray(thing)) {
            return WishFail.make('is not array');
        }
        const exits: Array<WishFail | WishCope<T>> = [];
        const output: Array<T> = [];
        for (let idx = 0; idx < thing.length; idx++) {
            if (!(idx in thing)) {
                exits.push(WishFail.make([{message: 'empty slot', at: [`[${idx}]`]}]));
            }
            const result = wish.call(thing[idx]);
            if (WishOk.is(result) || WishCope.is(result)) {
                output.push(result.output);
            }
            if (WishFail.is(result) || WishCope.is(result)) {
                exits.push(WishResult.extend(`[${idx}]`, result));
            }
        }
        if (exits.length) {
            return WishResult.aggregate(exits, output);
        }
        return WishOk.make(output);
    });
};

export const makeWishRecord = <V, K extends Key>(source: {key: WishLike<K>; value: WishLike<V>}) => {
    const keyWish = WishLike.normalize(source.key);
    const valWish = WishLike.normalize(source.value);

    type Out = {
        readonly [key in K]: V;
    };

    return Wish.make<Simplify<Out>>(function wishRecord(this: unknown, thing: unknown = this): WishResult<Out> {
        if (!isRecord(thing)) {
            return WishFail.make('is not record');
        }
        const exits: Array<WishFail | WishCope<void>> = [];
        const output: Array<[K, V]> = [];
        for (const key in thing) {
            const keyResult = keyWish.call(key);
            if (WishFail.is(keyResult)) {
                exits.push(WishResult.extend(`record key ${key}`, keyResult));
                continue;
            }
            const valResult = valWish.call(thing[key]);
            if (WishFail.is(valResult)) {
                exits.push(WishResult.extend(`.${key}`, valResult));
                continue;
            }
            let issues: WishIssue[] = [];
            if (WishCope.is(keyResult)) {
                issues = issues.concat(keyResult.issues);
            }
            if (WishCope.is(valResult)) {
                issues = issues.concat(valResult.issues);
            }
            output.push([keyResult.output, valResult.output]);
            if (issues.length) {
                exits.push(WishCope.make(issues, undefined));
            }
        }
        const outputValue = Object.fromEntries(output) as Out;
        if (exits.length > 0) {
            return WishResult.aggregate(exits, outputValue);
        }
        return WishOk.make(outputValue);
    });
};

export type StructDef = {
    readonly [key: string]: WishEntry<any, boolean> | WishLike<any>;
};
export type StructDefNormalized = {
    readonly [key: string]: WishEntry<any, boolean>;
};
export type StructOut<P extends StructDef> = Simplify<
    {
        readonly [K in keyof P as IfEntryOptional<P[K], never, K>]: Infer<P[K]>;
    } & {
        readonly [K in keyof P as IfEntryOptional<P[K], K, never>]?: Infer<P[K]>;
    }
>;

type IfEntryOptional<E, Then, Else> = E extends WishEntry<any, infer O> ? (O extends true ? Then : Else) : Else;

export const makeWishStruct = <D extends StructDef>(def: D) => {
    type T = StructOut<D>;
    const normalizedDef = Object.fromEntries(
        Object.entries(def).map(([key, value]) => {
            if (WishEntry.is(value)) {
                return [key, value];
            }
            return [key, WishEntry.make(value)];
        })
    ) as StructDefNormalized;

    return Wish.make<T>(function wishStruct(this: unknown, thing: unknown = this): WishResult<T> {
        if (!isRecord(thing)) {
            return WishFail.make('is not object');
        }
        const def = normalizedDef;

        const exits: Array<WishFail | WishCope<unknown>> = [];
        const out: Record<Key, unknown> = {};
        const entries = Object.entries(def);
        for (const [key, wishEntry] of entries) {
            const accessResult = wishEntry.accessor(thing, key);
            if (WishFail.is(accessResult)) {
                if (!wishEntry.optional) {
                    exits.push(accessResult);
                }
                continue;
            }
            if (WishCope.is(accessResult)) {
                exits.push(accessResult);
            }
            const entryResult = wishEntry.call(accessResult.output);
            if (WishOk.is(entryResult) || WishCope.is(entryResult)) {
                out[key] = entryResult.output;
            }
            if (WishFail.is(entryResult) || WishCope.is(entryResult)) {
                exits.push(WishResult.extend(`.${key}`, entryResult));
            }
        }
        if (exits.length) {
            return WishResult.aggregate(exits, out as T);
        }
        return WishOk.make(out as T);
    });
};

const WishEntryTypeId = Symbol.for('wish/EntryTypeId');

export interface WishEntry<out T, out O extends boolean>
    extends Wish.Callable<T>,
        Pipeable.Pipeable,
        WishEntry.Members<T, O>,
        WishEntry.Variance<T, O> {}

export namespace WishEntry {
    export const is = (thing: unknown): thing is WishEntry<any, any> =>
        thing != null && Reflect.has(thing, WishEntryTypeId);

    export const defaultAccessor: Accessor = (target, key) => {
        if (key in target) {
            return WishOk.make(target[key]);
        } else {
            return WishFail.make([{message: 'missing', at: [`.${String(key)}`]}]);
        }
    };

    export const defaultOptional = false;

    export function make<T, const O extends boolean = false>(
        source: WishLike<T>,
        members: Partial<Members<T, O>> = {}
    ): WishEntry<T, O> {
        const wish = WishLike.normalize(source);
        const accessor = members.accessor ?? (WishEntry.is(wish) ? wish.accessor : defaultAccessor);
        const optional = members.optional ?? (WishEntry.is(wish) ? wish.optional : defaultOptional);

        return {
            ...Proto,
            optional,
            accessor,
            call: wish.call,
        };
    }

    export const makeForKey = <T>(key: Key, wish: WishLike<T>): WishEntry<T, false> => {
        return make(wish, {
            accessor: target => defaultAccessor(target, key),
        });
    };

    export const makeOptional = <T>(wish: WishLike<T> | WishEntry<T, boolean>): WishEntry<T, true> => {
        return make(wish, {
            optional: true,
        });
    };

    export interface Accessor {
        (target: Record<Key, unknown>, key: Key): WishResult<unknown>;
    }

    export interface Members<out _T, out O extends boolean> {
        accessor: Accessor;
        optional: O;
    }

    export interface Variance<out T, out F> {
        readonly [WishEntryTypeId]: {readonly _T: Covariant<T>; readonly _O: Covariant<F>};
    }
    export const Proto: Variance<never, never> & Pipeable.Pipeable = {
        [WishEntryTypeId]: {_T: _ => _, _O: _ => _},
        ...Pipeable.Prototype,
    };
}

export const makeWishChoice = <D extends ReadonlyArray<WishLike<any>>>(...sources: D) => {
    const choices = sources.map(WishLike.normalize);
    type T = Infer<D[number]>;
    if (!choices.length) {
        return Wish.make<never>(function () {
            return WishFail.make('has no choices');
        });
    }
    return Wish.make<T>(function wishChoice(this: unknown, thing: unknown = this) {
        const fails: Array<WishFail> = [];
        let cope: WishCope<T> | undefined;
        let i = 0;
        for (const wish of choices) {
            i++;
            const out = wish.call(thing);
            if (WishOk.is(out)) {
                return out;
            }
            if (WishCope.is(out) && !cope) {
                cope = WishResult.extend(`#${i}`, out);
            }
            if (WishFail.is(out)) {
                fails.push(WishResult.extend(`#${i}`, out));
            }
        }
        if (cope) {
            return cope;
        }
        return WishResult.aggregateFail(fails);
    });
};

function makeIssues(messageOrIssues: string | readonly WishIssue[]): readonly WishIssue[] {
    return isString(messageOrIssues)
        ? messageOrIssues !== ''
            ? [{message: messageOrIssues, at: []}]
            : []
        : messageOrIssues;
}

export function makePrimitive<N extends keyof Primitives>(primitiveName: N) {
    type T = Primitives[N];
    return Wish.make<T>(function wishPrimitive(this: unknown, thing: unknown = this) {
        if (typeof thing === primitiveName) {
            return WishOk.make(thing as T);
        } else {
            return WishFail.make(`is ${kindOf(thing)} not ${primitiveName}`);
        }
    });
}

export const makeStandard = <S extends StandardSchemaV1>(schema: S) => {
    return Wish.make<StandardSchemaV1.InferOutput<S>>(function wishStandard(this: unknown, thing: unknown = this) {
        const result = schema['~standard'].validate(thing);
        if (result instanceof Promise) {
            throw new Error('Async StandardSchemaV1.Result is not supported');
        } else {
            return wrap(result);
        }
    });

    function wrap(result: Awaited<StandardSchemaV1.Result<StandardSchemaV1.InferInput<S>>>) {
        if ('value' in result) {
            return WishOk.make(result.value);
        }
        return WishFail.fromStandardIssues(result.issues);
    }
};

// ============================================================================
// UTILS
// ============================================================================

export const kindOf = (thing: any) => {
    if (thing === null) {
        return 'null';
    }
    return typeof thing;
};

export const indent = (message: string) => {
    return message
        .split('\n')
        .map(m => `\t${m}`)
        .join('\n');
};

export type real = NonNullable<unknown>;
export type some = real | null | undefined;
export type Key = keyof any;

export type primitive = Primitives[keyof Primitives];
export interface Primitives {
    null: null;
    undefined: undefined;
    symbol: symbol;
    boolean: boolean;
    number: number;
    string: string;
    bigint: bigint;
}
